import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import axios from "axios";

const CASES_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Owner deletes their own case via backend (verifies ownership + cascades
// applications so no orphans remain; blocked once a financial commitment exists).
export async function deleteOwnCase(caseId) {
  const token = await auth.currentUser.getIdToken();
  await axios.delete(`${CASES_API}/cases/${caseId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export const CASE_STATUS = [
  "Draft",
  "Published",
  "Designer Selected",
  "In Progress",
  "Submitted",
  "Completed",
];

const CASES = "cases";

// Fase D2: version of the per-case confidentiality confirmation a designer accepts
// when starting work. Stored on the designer's OWN user doc (users/{uid}) so no
// extra security rule is needed.
export const CASE_CONFIDENTIALITY_VERSION = "1.0";

export async function acceptCaseConfidentiality(uid, caseId) {
  await updateDoc(doc(db, "users", uid), {
    [`caseConfidentiality.${caseId}`]: {
      caseId,
      acceptedAt: Date.now(),
      version: CASE_CONFIDENTIALITY_VERSION,
    },
  });
}

// Fase 2: mark the case as paid after Stripe confirms the checkout. Written by the
// lab (owner) from the payment-success page. Gates designer work until paid.
export async function markCasePaid(caseId, { paymentTransactionId, paidAmount }) {
  await updateDoc(doc(db, CASES, caseId), {
    paymentStatus: "paid",
    paymentTransactionId: paymentTransactionId || "",
    paidAmount: paidAmount ?? null,
    paidAt: Date.now(),
    updatedAt: Date.now(),
  });
}

function sortByCreated(list) {
  return list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function createCase(data) {
  const payload = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const refDoc = await addDoc(collection(db, CASES), payload);
  await updateDoc(refDoc, { id: refDoc.id });
  return refDoc.id;
}

export async function updateCase(id, data) {
  await updateDoc(doc(db, CASES, id), { ...data, updatedAt: Date.now() });
}

export async function getCase(id) {
  const snap = await getDoc(doc(db, CASES, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listCasesByOwner(ownerId) {
  const q = query(collection(db, CASES), where("ownerId", "==", ownerId));
  const snap = await getDocs(q);
  return sortByCreated(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
}

// A case counts as demo/test if it carries ANY of these flags. Real cases have
// none of them. Owner-based demo detection is normalized into `isDemo` at the
// data source via the admin tag-demo-cases endpoint + at creation time.
export function isDemoCase(c) {
  return c?.isDemo === true || c?.isTest === true || c?.demo === true || c?.test === true;
}

export async function listPublishedCases({ includeDemo = false } = {}) {
  const q = query(collection(db, CASES), where("status", "==", "Published"));
  const snap = await getDocs(q);
  const list = sortByCreated(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  // Exclude demo/test cases AND admin-hidden/paused cases at the data layer so the
  // Marketplace only ever returns real, visible cases for normal users.
  return includeDemo ? list : list.filter((c) => !isDemoCase(c) && c.hidden !== true);
}

export async function listActiveCasesForDesigner(uid) {
  const q = query(collection(db, CASES), where("selectedDesignerId", "==", uid));
  const snap = await getDocs(q);
  return sortByCreated(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
}

export async function countCompletedCasesByDesigner(uid) {
  try {
    const q = query(collection(db, CASES), where("selectedDesignerId", "==", uid), where("status", "==", "Completed"));
    const snap = await getDocs(q);
    return snap.size;
  } catch {
    return 0;
  }
}

export async function updateStatus(id, status) {
  await updateDoc(doc(db, CASES, id), { status, updatedAt: Date.now() });
}

export async function deleteCase(id) {
  await deleteDoc(doc(db, CASES, id));
}

// ---- Private, confidential patient data (subcollection: cases/{id}/private/*) ----
// Two docs so the lab controls what the selected designer can see:
//   private/owner  -> full confidential data, readable ONLY by the owner (lab)
//   private/shared -> only the fields the lab chose to share (+ modelEngraving),
//                     readable by owner + selected designer.
export async function getCasePrivateOwner(caseId) {
  try {
    const snap = await getDoc(doc(db, CASES, caseId, "private", "owner"));
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
}

export async function getCasePrivateShared(caseId) {
  try {
    const snap = await getDoc(doc(db, CASES, caseId, "private", "shared"));
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
}

export async function setCasePrivate(caseId, ownerData, sharedData) {
  const ts = Date.now();
  await setDoc(doc(db, CASES, caseId, "private", "owner"), { ...ownerData, updatedAt: ts }, { merge: true });
  // Overwrite (not merge) shared so unchecking a field actually removes it.
  await setDoc(doc(db, CASES, caseId, "private", "shared"), { ...sharedData, updatedAt: ts });
}

// Unique, sequential DentalCAD Connect case id: DCC-<AccountCode>-<Year>-00001.
// The per-account counter lives on the lab's user doc and is bumped in a transaction.
export async function generateCaseId(accountCode, uid) {
  const userRef = doc(db, "users", uid);
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    const next = (snap.data()?.caseSeq || 0) + 1;
    tx.update(userRef, { caseSeq: next });
    return next;
  });
  const year = new Date().getFullYear();
  return `DCC-${accountCode}-${year}-${String(seq).padStart(5, "0")}`;
}

export async function submitWork(caseId, { submissionFiles, submissionNotes }) {
  // Keys must match the "selected designer" security rule (status, submissionFiles,
  // submissionNotes, updatedAt).
  await updateDoc(doc(db, CASES, caseId), {
    submissionFiles: submissionFiles || [],
    submissionNotes: submissionNotes || "",
    status: "Submitted",
    updatedAt: Date.now(),
  });
}

export async function selectDesigner(caseId, designer, agreement = {}) {
  await updateDoc(doc(db, CASES, caseId), {
    selectedDesignerId: designer.designerId,
    selectedDesignerName: designer.name,
    selectedDesignerEmail: designer.email || "",
    status: "Designer Selected",
    // Agreed commercial terms (snapshot). Connect-ready; payout stays manual in the MVP.
    agreedLabPrice: agreement.labPrice ?? null,
    agreedDesignerNet: agreement.designerNet ?? null,
    agreedCommissionRate: agreement.commissionRate ?? null,
    agreedCommissionAmount: agreement.commissionAmount ?? null,
    agreedOfferType: agreement.offerType || "accept",
    agreedCurrency: "USD",
    // Payment lifecycle (Phase 2 fills these; initialized here for a clean data model).
    paymentStatus: "unpaid",
    payoutStatus: "pending",
    updatedAt: Date.now(),
  });
}

// Un-assign the current designer and re-open the case to the marketplace.
// Only meaningful while the designer has NOT started work (status "Designer Selected").
export async function changeDesigner(caseId) {
  await updateDoc(doc(db, CASES, caseId), {
    selectedDesignerId: "",
    selectedDesignerName: "",
    status: "Published",
    updatedAt: Date.now(),
  });
}

// ---------------- Applications (top-level collection) ----------------
const APPS = "applications";
const appId = (caseId, designerId) => `${caseId}_${designerId}`;

export async function applyToCase(caseId, application) {
  await setDoc(doc(db, APPS, appId(caseId, application.designerId)), {
    caseId,
    designerId: application.designerId,
    name: application.name,
    email: application.email || "",
    message: application.message || "",
    // Commercial offer (Phase 1). offerType: 'accept' (take the offered price) or
    // 'counter' (designer states the NET they want; the lab price is derived).
    offerType: application.offerType || "accept",
    designerNet: application.designerNet ?? null,
    labPrice: application.labPrice ?? null,
    commissionRate: application.commissionRate ?? null,
    commissionAmount: application.commissionAmount ?? null,
    createdAt: Date.now(),
  });
}

export async function getApplication(caseId, designerId) {
  try {
    const snap = await getDoc(doc(db, APPS, appId(caseId, designerId)));
    return snap.exists() ? snap.data() : null;
  } catch {
    // Firestore denies reads on a non-existent doc (rule references resource.data);
    // semantically this just means "not applied yet".
    return null;
  }
}

export async function listApplications(caseId) {
  const q = query(collection(db, APPS), where("caseId", "==", caseId));
  const snap = await getDocs(q);
  return sortByCreated(snap.docs.map((d) => d.data()));
}

export async function listApplicationsByDesigner(uid) {
  const q = query(collection(db, APPS), where("designerId", "==", uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}
